import { inject, Injectable } from '@angular/core';
import { CustomerService } from '../customer.service';
import { MODAL, ModalService } from '../modal.service';

const CIAM_SESSION_EXPIRED_MESSAGES = {
  expired: 'popins.ciam-session-expired.message',
  popupBlocked: 'popins.ciam-session-expired.popup-blocked',
  stillSignedOut: 'popins.ciam-session-expired.still-signed-out',
} as const;

/**
 * Reprise d'une session CIAM expirée depuis un écran qui porte un travail non rejouable.
 *
 * Renvoyer le client vers AuthApp en pleine page perdrait la configuration en cours : AuthApp est
 * donc ouvert dans un onglet séparé, et l'action interrompue est rejouée ici même. Le cookie de
 * session posé par AuthApp étant partagé entre les onglets — contrairement au sessionStorage —,
 * il suffit de ré-échanger la session au retour du client.
 */
@Injectable({ providedIn: 'root' })
export class CiamReconnectionService {
  private readonly customer = inject(CustomerService);
  private readonly modalService = inject(ModalService);

  /**
   * @param retry Action interrompue par l'expiration, à rejouer une fois la session reprise.
   */
  prompt(retry: () => Promise<void>): void {
    this.display(CIAM_SESSION_EXPIRED_MESSAGES.expired, retry);
  }

  private display(contentRef: string, retry: () => Promise<void>): void {
    this.modalService.display(MODAL.MESSAGE, {
      titleRef: 'popins.ciam-session-expired.title',
      contentRef,
      buttons: [
        {
          labelRef: 'popins.ciam-session-expired.retry',
          primary: false,
          action: () => this.resumeThenRetry(retry),
        },
        {
          labelRef: 'popins.ciam-session-expired.reconnect',
          primary: true,
          action: () => this.openAuthApp(retry),
        },
      ],
    });
  }

  /** Aucun `await` avant l'ouverture : elle doit rester rattachée au clic, sinon elle est bloquée. */
  private openAuthApp(retry: () => Promise<void>): void {
    if (this.customer.loginInNewTab()) {
      // La modale reste affichée : c'est elle qui porte la reprise, une fois le client revenu.
      return;
    }

    this.display(CIAM_SESSION_EXPIRED_MESSAGES.popupBlocked, retry);
  }

  private async resumeThenRetry(retry: () => Promise<void>): Promise<void> {
    this.modalService.close();

    if (!(await this.customer.resumeCiamSession())) {
      this.display(CIAM_SESSION_EXPIRED_MESSAGES.stillSignedOut, retry);
      return;
    }

    await retry();
  }
}
